import React, { useState } from 'react';
import { PromptService, PromptAPIError } from '../../services/prompts';

interface ImportPreview {
  total: number;
  valid: number;
  invalid: number;
  items: Array<{
    title: string;
    content: string;
    description: string;
    category: string;
    tags: string;
    isValid: boolean;
    error?: string;
  }>;
}

interface ImportPageProps {
  onNavigate?: (page: string) => void;
}

export const ImportPage: React.FC<ImportPageProps> = ({ onNavigate }) => {
  const [importText, setImportText] = useState('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: number; failed: number } | null>(null);

  const exampleData = [
    {
      title: "商业计划书生成器",
      content: "请帮我制作一份关于{业务类型}的详细商业计划书，包含市场分析、竞争分析、财务预测等内容。",
      description: "用于生成专业的商业计划书模板",
      category: "商业策略",
      tags: "计划书, 市场分析, 财务预测, 商业模式"
    },
    {
      title: "技术文档撰写助手", 
      content: "为{产品名称}编写技术文档，需要包含功能介绍、使用方法、API文档等。",
      description: "协助撰写清晰的技术文档",
      category: "技术开发",
      tags: "文档写作, API文档, 产品说明, 技术规范"
    },
    {
      title: "社交媒体文案创作",
      content: "为{品牌/产品}创建吸引人的社交媒体文案，包括微博、微信、抖音等平台的内容。",
      description: "专门用于社交媒体营销的文案创作",
      category: "市场营销",
      tags: "社交媒体, 文案创作, 品牌营销, 内容运营"
    }
  ];

  const validateImportData = (data: any[]): ImportPreview => {
    const items = data.map(item => {
      let isValid = true;
      let error = '';

      if (!item.content || typeof item.content !== 'string') {
        isValid = false;
        error = '缺少或无效的内容字段';
      }

      return {
        title: item.title || `提示词_${Date.now()}`,
        content: item.content || '',
        description: item.description || '',
        category: item.category || '',
        tags: item.tags || '',
        isValid,
        error
      };
    });

    return {
      total: items.length,
      valid: items.filter(item => item.isValid).length,
      invalid: items.filter(item => !item.isValid).length,
      items
    };
  };

  const handlePreview = () => {
    try {
      const data = JSON.parse(importText);
      if (!Array.isArray(data)) {
        throw new Error('数据必须是数组格式');
      }
      const previewData = validateImportData(data);
      setPreview(previewData);
    } catch (error) {
      alert(`JSON格式错误: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  };

  const handleImport = async () => {
    if (!preview) return;

    setIsImporting(true);
    let successCount = 0;
    let failedCount = 0;

    try {
      // 逐个导入有效的提示词
      for (const item of preview.items) {
        if (!item.isValid) {
          failedCount++;
          continue;
        }

        try {
          await PromptService.createPrompt({
            title: item.title || undefined,
            content: item.content,
            description: item.description || undefined,
            category: item.category || undefined,
            tags: item.tags || undefined
          });
          successCount++;
        } catch (error) {
          console.error('导入单个提示词失败:', error);
          failedCount++;
        }
      }
      
      setImportResult({
        success: successCount,
        failed: failedCount
      });
      setPreview(null);
      setImportText('');
    } catch (error) {
      const err = error as PromptAPIError;
      alert(`导入失败: ${err.message || '未知错误'}`);
    } finally {
      setIsImporting(false);
    }
  };

  const copyExampleData = () => {
    setImportText(JSON.stringify(exampleData, null, 2));
  };

  return (
    <div className="space-y-6">
      {/* 页面标题和返回按钮 */}
      <div className="flex items-center space-x-4">
        {onNavigate && (
          <button
            onClick={() => onNavigate('prompts')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            返回提示词库
          </button>
        )}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">批量导入</h1>
          <p className="mt-1 text-sm text-gray-500">
            通过JSON格式批量导入提示词数据
          </p>
        </div>
      </div>

      {/* 格式说明 */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">数据格式说明</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>请上传JSON格式的数组，每个对象包含以下字段：</p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li><code className="bg-blue-100 px-1 rounded">title</code>: 提示词标题（可选，为空时自动生成）</li>
                <li><code className="bg-blue-100 px-1 rounded">content</code>: 提示词内容（必填）</li>
                <li><code className="bg-blue-100 px-1 rounded">description</code>: 描述信息（可选）</li>
                <li><code className="bg-blue-100 px-1 rounded">category</code>: 业务分类，选择所属领域（可选）</li>
                <li><code className="bg-blue-100 px-1 rounded">tags</code>: 功能标签，多个标签用逗号分隔（可选）</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 示例数据 */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">示例数据</h3>
          <button
            onClick={copyExampleData}
            className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            复制示例
          </button>
        </div>
        <pre className="bg-gray-50 p-3 rounded border text-sm overflow-x-auto">
          <code>{JSON.stringify(exampleData, null, 2)}</code>
        </pre>
      </div>

      {/* 导入区域 */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-medium text-gray-900 mb-4">导入数据</h3>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="import-data" className="block text-sm font-medium text-gray-700 mb-2">
              JSON数据
            </label>
            <textarea
              id="import-data"
              rows={12}
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              className="block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              placeholder="请粘贴JSON格式的提示词数据..."
            />
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handlePreview}
              disabled={!importText.trim()}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              预览导入数据
            </button>
          </div>
        </div>
      </div>

      {/* 预览结果 */}
      {preview && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">导入预览</h3>
            <div className="text-sm text-gray-600">
              总计 {preview.total} 项 • 有效 {preview.valid} 项 • 无效 {preview.invalid} 项
            </div>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {preview.items.map((item, index) => (
              <div
                key={index}
                className={`p-3 rounded border ${
                  item.isValid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{item.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                    <p className="text-xs text-gray-500 mt-1 font-mono bg-white p-2 rounded">
                      {item.content.substring(0, 100)}...
                    </p>
                    {item.tags && (
                      <div className="mt-2">
                        {item.tags.split(',').map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-1"
                          >
                            {tag.trim()}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="ml-3">
                    {item.isValid ? (
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                </div>
                {!item.isValid && item.error && (
                  <p className="text-sm text-red-600 mt-2">{item.error}</p>
                )}
              </div>
            ))}
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              onClick={handleImport}
              disabled={preview.valid === 0 || isImporting}
              className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {isImporting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  导入中...
                </>
              ) : (
                `确认导入 (${preview.valid} 项)`
              )}
            </button>
            <button
              onClick={() => setPreview(null)}
              className="bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* 导入结果 */}
      {importResult && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">导入完成</h3>
              <div className="mt-2 text-sm text-green-700">
                <p>成功导入 {importResult.success} 个提示词</p>
                {importResult.failed > 0 && (
                  <p>跳过 {importResult.failed} 个无效项目</p>
                )}
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setImportResult(null)}
                  className="bg-green-100 px-3 py-1 rounded text-green-800 text-sm hover:bg-green-200 transition-colors"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 